import { useState, useEffect, useMemo } from 'react';
import { fabricsAPI, settingsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Search, Sparkles, ArrowRight, Eye } from 'lucide-react';

const GARMENT_TYPES = [
  { value: 'suit', label: 'Suit' },
  { value: 'jacket', label: 'Jacket' },
  { value: 'shirt', label: 'Shirt' },
  { value: 'sherwani', label: 'Sherwani' },
  { value: 'bandhgala', label: 'Bandhgala' },
  { value: 'nehru_vest', label: 'Nehru Vest' },
  { value: 'kurta', label: 'Kurta' },
  { value: 'trousers', label: 'Trousers' },
];

export default function StepFabrics({ wizardState, updateState, nextStep }) {
  const [fabrics, setFabrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterColour, setFilterColour] = useState('all');
  const [filterPattern, setFilterPattern] = useState('all');
  const [filterSeason, setFilterSeason] = useState('all');
  const [makingCharges, setMakingCharges] = useState({});
  const [filterOptions, setFilterOptions] = useState({ colours: [], patterns: [], seasons: [] });

  useEffect(() => {
    const load = async () => {
      try {
        const [fabRes, filterRes, mcRes] = await Promise.all([
          fabricsAPI.list({ limit: 500, status: 'active' }),
          fabricsAPI.getFilterOptions(),
          settingsAPI.get('making_charges'),
        ]);
        setFabrics(fabRes.data?.fabrics || []);
        const fo = filterRes.data || {};
        setFilterOptions({
          colours: fo.colours || [],
          patterns: fo.pattern_types || [],
          seasons: fo.seasons || [],
        });
        setMakingCharges(mcRes.data?.value || {});
      } catch (err) {
        toast.error('Failed to load fabrics');
      }
      setLoading(false);
    };
    load();
  }, []);

  const garmentType = wizardState.selectedGarmentType || 'suit';

  const getInclusivePrice = (fabric) => {
    const fabricCost = fabric.price_per_metre || 0;
    const consumption = garmentType === 'suit' ? 4.5 : garmentType === 'shirt' ? 2.5 : garmentType === 'trousers' ? 1.8 : 3.5;
    const fabricTotal = fabricCost * consumption;
    const making = makingCharges[garmentType] || 3000;
    return Math.round(fabricTotal + making);
  };

  const filtered = useMemo(() => {
    let result = fabrics;
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(f =>
        (f.mill_name || '').toLowerCase().includes(q) ||
        (f.colour || '').toLowerCase().includes(q) ||
        (f.pattern || '').toLowerCase().includes(q) ||
        (f.description || '').toLowerCase().includes(q)
      );
    }
    if (filterColour !== 'all') result = result.filter(f => f.colour === filterColour);
    if (filterPattern !== 'all') result = result.filter(f => f.pattern === filterPattern);
    if (filterSeason !== 'all') result = result.filter(f => (f.season_tags || []).includes(filterSeason));
    return result;
  }, [fabrics, search, filterColour, filterPattern, filterSeason]);

  const handleSelect = (fabric) => {
    const price = getInclusivePrice(fabric);
    updateState({
      selectedFabric: fabric,
      inclusivePrice: price,
    });
  };

  const handleProceed = () => {
    if (!wizardState.selectedFabric) {
      toast.error('Please select a fabric first');
      return;
    }
    nextStep();
  };

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-12 py-10" data-testid="step-fabrics">
      {/* Heading */}
      <div className="text-center mb-10">
        <h1 className="text-3xl lg:text-4xl font-light tracking-tight" style={{ color: 'var(--brand-navy)', fontFamily: "'Playfair Display', serif" }}>
          Choose Your Fabric
        </h1>
        <p className="mt-2 text-base" style={{ color: 'var(--ink-muted)' }}>
          Browse our collection of premium fabrics from the world's finest mills
        </p>
      </div>

      {/* Garment type + Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-8">
        <div className="flex items-center gap-2 mr-4">
          <span className="text-sm font-medium" style={{ color: 'var(--brand-navy)' }}>I'm looking for a</span>
          <Select value={garmentType} onValueChange={v => updateState({ selectedGarmentType: v })}>
            <SelectTrigger className="w-40 h-10 rounded-full border-2 text-sm font-semibold" style={{ borderColor: 'var(--brand-gold)', color: 'var(--brand-navy)' }}
              data-testid="garment-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {GARMENT_TYPES.map(g => <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--ink-muted)' }} />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by colour, mill, pattern..."
            className="h-10 pl-10 rounded-full text-sm"
            data-testid="fabric-search-input"
          />
        </div>

        <Select value={filterColour} onValueChange={setFilterColour}>
          <SelectTrigger className="w-32 h-10 rounded-full text-xs"><SelectValue placeholder="Colour" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Colours</SelectItem>
            {filterOptions.colours.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={filterPattern} onValueChange={setFilterPattern}>
          <SelectTrigger className="w-32 h-10 rounded-full text-xs"><SelectValue placeholder="Pattern" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Patterns</SelectItem>
            {filterOptions.patterns.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={filterSeason} onValueChange={setFilterSeason}>
          <SelectTrigger className="w-32 h-10 rounded-full text-xs"><SelectValue placeholder="Season" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Seasons</SelectItem>
            {filterOptions.seasons.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Fabric Grid */}
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="aspect-square rounded-2xl" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20">
          <p className="text-lg" style={{ color: 'var(--ink-muted)' }}>No fabrics match your search</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {filtered.map(fabric => {
            const isSelected = wizardState.selectedFabric?.id === fabric.id;
            const price = getInclusivePrice(fabric);
            const swatchUrl = fabric.swatch_image_url || fabric.swatch_url;

            return (
              <div
                key={fabric.id}
                className="group relative rounded-2xl overflow-hidden transition-all duration-300 cursor-pointer"
                style={{
                  border: isSelected ? '3px solid var(--brand-gold)' : '1px solid rgba(27,42,74,0.08)',
                  boxShadow: isSelected ? '0 0 0 4px rgba(197,165,90,0.15), 0 8px 24px rgba(0,0,0,0.1)' : '0 2px 8px rgba(0,0,0,0.04)',
                  transform: isSelected ? 'scale(1.02)' : 'scale(1)',
                }}
                onClick={() => handleSelect(fabric)}
                data-testid={`fabric-card-${fabric.id}`}
              >
                {/* Swatch Image */}
                <div className="aspect-square bg-gray-100 relative overflow-hidden">
                  {swatchUrl ? (
                    <img
                      src={swatchUrl}
                      alt={fabric.description || fabric.mill_name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: 'rgba(27,42,74,0.04)' }}>
                      <Sparkles className="w-10 h-10 opacity-15" />
                    </div>
                  )}

                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex gap-2">
                      <Button size="sm" className="rounded-full h-9 text-xs gap-1.5 shadow-lg" style={{ backgroundColor: 'var(--brand-gold)', color: 'var(--brand-navy)' }}
                        data-testid={`try-on-btn-${fabric.id}`}
                        onClick={(e) => { e.stopPropagation(); handleSelect(fabric); nextStep(); }}>
                        <Eye className="w-3.5 h-3.5" /> Try On
                      </Button>
                    </div>
                  </div>

                  {/* Selected badge */}
                  {isSelected && (
                    <div className="absolute top-3 right-3">
                      <Badge className="rounded-full px-3 py-1 text-[10px] font-bold shadow-lg" style={{ backgroundColor: 'var(--brand-gold)', color: 'var(--brand-navy)' }}>
                        Selected
                      </Badge>
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="p-4 bg-white">
                  <p className="text-sm font-semibold truncate" style={{ color: 'var(--brand-navy)' }}>
                    {fabric.mill_name || 'Premium Fabric'}
                  </p>
                  <p className="text-xs mt-0.5 truncate" style={{ color: 'var(--ink-muted)' }}>
                    {[fabric.colour, fabric.pattern].filter(Boolean).join(' · ')}
                  </p>
                  <p className="text-base font-bold mt-2 tabular-nums" style={{ color: 'var(--brand-navy)' }}>
                    &#8377;{price.toLocaleString('en-IN')}
                  </p>
                  <p className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>
                    Inclusive of fabric & stitching
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Bottom action bar */}
      {wizardState.selectedFabric && (
        <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t py-4 px-8 z-40"
          style={{ borderColor: 'rgba(27,42,74,0.08)' }}>
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <div>
              <p className="text-sm" style={{ color: 'var(--ink-muted)' }}>Selected:</p>
              <p className="text-base font-semibold" style={{ color: 'var(--brand-navy)' }}>
                {wizardState.selectedFabric.mill_name} — {wizardState.selectedFabric.colour} {wizardState.selectedFabric.pattern}
              </p>
            </div>
            <div className="flex items-center gap-4">
              <p className="text-xl font-bold tabular-nums" style={{ color: 'var(--brand-navy)' }}>
                &#8377;{wizardState.inclusivePrice?.toLocaleString('en-IN')}
              </p>
              <Button
                onClick={handleProceed}
                className="h-12 px-8 rounded-full text-sm font-semibold gap-2 shadow-lg"
                style={{ backgroundColor: 'var(--brand-navy)' }}
                data-testid="proceed-to-client-btn"
              >
                Select Fabric <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
